import type { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { API_AXIOS, INCIDENCE_API_AXIOS } from '@/constants/axios';
import { ENV_API } from '@/constants/env';
import { HEADER_LOCALE, HEADER_MARKET } from '@/constants/headers';
import { DEFAULT_LANGUAGE, getLocaleCode } from '@/constants/languages';
import { ROUTES } from '@/constants/routes';
import { useAuthStore } from '@/features/auth/stores/useAuthStore';
import { getAuthToken } from '@/features/auth/utils/auth-data';
import { useMarketStore } from '@/features/markets/stores/useMarketStore';
import { useLocaleStore } from '@/features/pageContainer/stores/useLocaleStore';
import { PaginatedResponse } from '@/hooks/usePagination.types';
import { t } from '@/i18n';
import { ObjectAny } from '@/models/types';
import { getAxiosBackendError, sanitizeErrorMessage } from '@/utils/errors';
import { showSnackbarError } from '@/utils/snackbar';

const ALLOWED_NON_MARKET_URLS: string[] = ['test'];

// Interceptor REQUEST

export async function interceptorApiAxiosRequest(config: InternalAxiosRequestConfig) {
    // authorization
    const accessToken = getAuthToken();
    if (accessToken) {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
    }

    // market
    const marketId = useMarketStore.getState().market?.id;
    if (marketId) {
        config.headers[HEADER_MARKET] = marketId;
    } else if (!ALLOWED_NON_MARKET_URLS.some((url) => config.url?.includes(url))) {
        // eslint-disable-next-line no-console
        console.warn(`Request sin marketId: ${config.method?.toUpperCase()} ${config.url}`);
    }

    // language
    config.headers[HEADER_LOCALE] = getLocaleCode(useLocaleStore.getState().locale ?? DEFAULT_LANGUAGE);

    return config;
}

// Interceptor RESPONSE

// NOTE: Si la respuesta es exitosa, simplemente retorna la respuesta.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function interceptorApiAxiosResponseOnFulfilled(response: any) {
    return response;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function interceptorApiAxiosResponseOnRejected(error: any) {
    // 401 Authorization Error

    const isNonAuthorizedError = error.response?.status === 401;
    const isClientSide = typeof window !== 'undefined' && window.location !== undefined;
    const isLoginCurrentPage = isClientSide ? window.location.pathname === ROUTES.login.route : false;

    if (error.config.throwError !== false && isClientSide && isNonAuthorizedError && !isLoginCurrentPage) {
        // NOTE: Here we only remove-auth, and is in RequireAuth.tsx where check-auth and redirect
        // TODO: Thinking in "remember", instead to remove-aut, it would be better to mark store-auth as "session-expired",
        //       and in RequireAuth.tsx check-auth, if "remember", try to refresh-token, if not, remove-auth.

        showSnackbarError(t('sessionExpired'), 'axios-session-expired');
        useAuthStore.getState().resetAuthState();

        return Promise.reject(error);
    }

    // Canceled Error

    const isCanceledError = error?.code === 'ERR_CANCELED';
    if (isCanceledError) {
        return error.config.throwError !== false ? Promise.reject(error) : undefined;
    }

    // Snackbar Error

    if (error.config.withSnackbarError !== false) {
        // Snackbar for form-validations error
        const errorsObject = error?.response?.data?.errors;
        if (errorsObject) {
            Object.entries(errorsObject).forEach(([key, value]) => {
                // Try to translate error key if possible
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const field = t(`${key}` as any);
                showSnackbarError(sanitizeErrorMessage(`${field}: ${value}`));
            });
        }
        // Snackbar for default error
        else {
            const apiError = getAxiosBackendError(error, true);
            showSnackbarError(sanitizeErrorMessage(apiError.message));
        }
    }

    return error.config.throwError !== false ? Promise.reject(error) : undefined;
}

//

API_AXIOS.interceptors.request.use(interceptorApiAxiosRequest);
API_AXIOS.interceptors.response.use(interceptorApiAxiosResponseOnFulfilled, interceptorApiAxiosResponseOnRejected);

//

const sanitizeIncidencePagination = (response: ObjectAny): PaginatedResponse => ({
    currentPage: response['hydra:view']['hydra:current'] ?? 1,
    items: response['hydra:member'],
    lastPage: response['hydra:view']['hydra:last'] ?? 1,
    pageItems: response['hydra:view']['hydra:count'] ?? 1,
    totalItems: response['hydra:totalItems'],
});

export interface HttpClientConfig<TPayload> extends AxiosRequestConfig<TPayload> {
    envApi?: ENV_API;
    withIncidencePagination?: boolean;
    withSnackbarError?: boolean;
    throwError?: boolean;
}

export class HttpClient {
    static async get<TResponse, TPayload = unknown>(url: string, config?: HttpClientConfig<TPayload>) {
        const apiAxios = config?.envApi === 'INCIDENCE_API' ? INCIDENCE_API_AXIOS : API_AXIOS;
        const response = await apiAxios.get<TResponse, AxiosResponse<TResponse>, TPayload>(url, config);

        // NOTE: 'response' has '?' to avoid undefined-error in endpoint-tests
        return (response?.data as ObjectAny)?.['hydra:member']
            ? config?.withIncidencePagination
                ? (sanitizeIncidencePagination(response?.data as ObjectAny) as TResponse)
                : ((response?.data as ObjectAny)?.['hydra:member'] as TResponse)
            : response?.data;
    }

    static async post<TResponse, TPayload = unknown>(
        url: string,
        data?: TPayload,
        config?: HttpClientConfig<TPayload>,
    ) {
        const apiAxios = config?.envApi === 'INCIDENCE_API' ? INCIDENCE_API_AXIOS : API_AXIOS;
        const response = await apiAxios.post<TResponse, AxiosResponse<TResponse>, TPayload>(url, data, config);

        return (response?.data as ObjectAny)?.['hydra:member']
            ? ((response?.data as ObjectAny)?.['hydra:member'] as TResponse)
            : response?.data;
    }

    static async put<TResponse, TPayload = unknown>(url: string, data: TPayload, config?: HttpClientConfig<TPayload>) {
        const apiAxios = config?.envApi === 'INCIDENCE_API' ? INCIDENCE_API_AXIOS : API_AXIOS;
        const response = await apiAxios.put<TResponse, AxiosResponse<TResponse>, TPayload>(url, data, config);

        return (response?.data as ObjectAny)?.['hydra:member']
            ? ((response?.data as ObjectAny)?.['hydra:member'] as TResponse)
            : response?.data;
    }

    static async patch<TResponse, TPayload = unknown>(
        url: string,
        data: TPayload,
        config?: HttpClientConfig<TPayload>,
    ) {
        const apiAxios = config?.envApi === 'INCIDENCE_API' ? INCIDENCE_API_AXIOS : API_AXIOS;
        const response = await apiAxios.patch<TResponse, AxiosResponse<TResponse>, TPayload>(url, data, config);

        return (response?.data as ObjectAny)?.['hydra:member']
            ? ((response?.data as ObjectAny)?.['hydra:member'] as TResponse)
            : response?.data;
    }

    static async delete<TResponse, TPayload = unknown>(url: string, config?: HttpClientConfig<TPayload>) {
        const apiAxios = config?.envApi === 'INCIDENCE_API' ? INCIDENCE_API_AXIOS : API_AXIOS;
        const response = await apiAxios.delete<TResponse, AxiosResponse<TResponse>, TPayload>(url, config);

        return (response?.data as ObjectAny)?.['hydra:member']
            ? ((response?.data as ObjectAny)?.['hydra:member'] as TResponse)
            : response?.data;
    }
}
